import { Skeleton } from "@design";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { getContentFilterAnalyticsOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { Text } from "@/components/Text";
import { AdminPage } from "../AdminPage";
import { RangeSelect, rangeStart } from "../usage/usageRange";
import { CategoryBreakdown, SeverityBreakdown, TotalsRow } from "./ContentSafetyBreakdowns";
import { type EventFilters, fillDays, filtersToParams, parseFilters } from "./contentSafety";
import { EventsSection } from "./EventsSection";
import { HitsTrend } from "./HitsTrend";

/**
 * /admin/content-safety: the Azure content filter's hits, from our own records (no Azure call, unlike
 * Governance). Range and filters live in the URL so a view can be shared and survives a refresh.
 */
export function ContentSafetyPage() {
  const { t } = useTranslation("adminContentSafety");
  const [params, setParams] = useSearchParams();
  const filters = parseFilters(params);
  const setFilters = (patch: Partial<EventFilters>) =>
    setParams(filtersToParams({ ...filters, ...patch }), { replace: true });
  const start = rangeStart(filters.range);
  const analytics = useQuery({
    ...getContentFilterAnalyticsOptions({ query: { start_date: start } }),
    placeholderData: keepPreviousData,
  });
  const data = analytics.data?.data;

  const body = () => {
    if (analytics.isPending) return <Skeleton className="h-16 w-full" />;
    if (analytics.isError) {
      return (
        <Text tone="error" role="alert">
          {t("loadFailed")}
        </Text>
      );
    }
    if (!data?.totals.events) return <EmptyState icon={<ShieldCheck size={32} />} title={t("empty")} />;
    return (
      <>
        <TotalsRow totals={data.totals} />
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <CategoryBreakdown categories={data.byCategory} />
          <SeverityBreakdown severities={data.bySeverity} />
        </div>
        <HitsTrend days={fillDays(start, new Date(), data.trend)} />
        <EventsSection
          start={start}
          filters={filters}
          categories={data.byCategory.map((category) => category.category)}
          onFiltersChange={setFilters}
        />
      </>
    );
  };

  return (
    <AdminPage title={t("title")} subtitle={t("subtitle")}>
      <RangeSelect label={t("range")} value={filters.range} onChange={(range) => setFilters({ range })} />
      {body()}
    </AdminPage>
  );
}
